import { PdfTable } from 'pages/v2/compiler/PDF/Styles.styled';
import React from 'react';
import { ScenarioModuleReadModel } from 'types/types';
import { EpdVersionHistoryData } from '../../version-history/utils/EpdVersionHistoryDataContext';
import { SummaryGridRow } from '../../version-history/VersionHistorySummaryGrid';

interface ScenarioModuleDescriptionPdfProps {
  module: string;
  moduleName: string;
  isDefaultScenario: boolean;
  scenarioModule: ScenarioModuleReadModel | undefined;
  isEPDComplete?: boolean;
}

const ScenarioModuleDescriptionPdf: React.FC<ScenarioModuleDescriptionPdfProps> = ({
  module,
  moduleName,
  isDefaultScenario,
  scenarioModule,
  isEPDComplete,
}) => {
  const defaultScenarioModuleBriefDescription = scenarioModule?.defaultScenarioModuleBriefDescription;
  const defaultScenarioModuleDescription = scenarioModule?.defaultScenarioModuleDescription;
  const filledRows = scenarioModule?.scenarioModuleItems?.filter((x) => x.unitId && x.value && x.description);

  const isEmpty = (value: string | undefined | null) => {
    return value === null || value === undefined || value.trim() === '';
  };

  const hideRSLDescription =
    filledRows?.length === scenarioModule?.scenarioModuleItems.length &&
    isEmpty(scenarioModule?.defaultScenarioModuleDescription) &&
    module === 'RSL';

  const bothDescriptionsEmpty =
    !isEPDComplete && isEmpty(defaultScenarioModuleBriefDescription) && isEmpty(defaultScenarioModuleDescription);

  const shouldShowDefaultScenarioModuleBriefDescription =
    bothDescriptionsEmpty || (!isEmpty(defaultScenarioModuleBriefDescription) && isEmpty(defaultScenarioModuleDescription));

  const shouldShowDefaultScenarioModuleDescription =
    (bothDescriptionsEmpty && isDefaultScenario) || !isEmpty(defaultScenarioModuleDescription);

  const shouldShowExplanatoryNameOrBriefDescription = isDefaultScenario && module !== 'RSL';

  const shouldRenderTable =
    shouldShowExplanatoryNameOrBriefDescription ||
    shouldShowDefaultScenarioModuleDescription ||
    shouldShowExplanatoryNameOrBriefDescription;

  if (!shouldRenderTable) {
    return null;
  }

  const rows = getScenarioModuleGridRows({} as EpdVersionHistoryData, true, {
    module,
    moduleName,
    isDefaultScenario,
    scenarioModule,
    isEPDComplete,
  });

  return (
    <PdfTable style={{ marginBottom: 15 }}>
      <thead>
        <tr>
          <th style={{ width: '30%' }}></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td style={{ fontSize: '12px', padding: '15px' }}>{row.name}</td>
            <td style={{ fontSize: '12px', padding: '15px' }}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </PdfTable>
  );
};

export default ScenarioModuleDescriptionPdf;

export const getScenarioModuleGridRows = (
  data: EpdVersionHistoryData,
  isPcrV2: boolean,
  additionalData?: any
): SummaryGridRow[] => {
  const defaultScenarioModuleBriefDescription = additionalData?.scenarioModule?.defaultScenarioModuleBriefDescription;
  const defaultScenarioModuleDescription = additionalData?.scenarioModule?.defaultScenarioModuleDescription;
  const filledRows = additionalData?.scenarioModule?.scenarioModuleItems?.filter(
    (x: any) => x.unitId && x.value && x.description
  );

  const isEmpty = (value: string | undefined | null) => {
    return value === null || value === undefined || value.trim() === '';
  };

  const hideRSLDescription =
    filledRows?.length === additionalData?.scenarioModule?.scenarioModuleItems.length &&
    isEmpty(additionalData?.scenarioModule?.defaultScenarioModuleDescription) &&
    additionalData?.module === 'RSL';

  const bothDescriptionsEmpty =
    !additionalData?.isEPDComplete &&
    isEmpty(defaultScenarioModuleBriefDescription) &&
    isEmpty(defaultScenarioModuleDescription);

  const shouldShowDefaultScenarioModuleBriefDescription =
    bothDescriptionsEmpty || (!isEmpty(defaultScenarioModuleBriefDescription) && isEmpty(defaultScenarioModuleDescription));

  const shouldShowDefaultScenarioModuleDescription =
    (bothDescriptionsEmpty && additionalData?.isDefaultScenario) || !isEmpty(defaultScenarioModuleDescription);

  const shouldShowExplanatoryNameOrBriefDescription = additionalData?.isDefaultScenario && additionalData?.module !== 'RSL';

  const rows: (SummaryGridRow | false)[] = [];

  if (shouldShowExplanatoryNameOrBriefDescription) {
    rows.push({
      ord: 1,
      name: `Explanatory name of the default scenario in module ${additionalData?.moduleName}`,
      value: additionalData?.scenarioModule?.defaultScenarioModuleExplanatoryName,
    });

    if (shouldShowDefaultScenarioModuleBriefDescription) {
      rows.push({
        ord: 2,
        name: `Brief description of the default scenario in module ${additionalData?.moduleName}`,
        value: additionalData?.scenarioModule?.defaultScenarioModuleBriefDescription,
      });
    }
  }

  if (shouldShowDefaultScenarioModuleDescription && !hideRSLDescription) {
    const descriptionLabel = `Description of the ${
      additionalData?.isDefaultScenario ? 'default' : 'additional'
    } scenario in${additionalData?.module === 'RSL' ? ' reference service life' : ` module ${additionalData?.moduleName}`}`;

    rows.push({
      ord: 3,
      name: descriptionLabel,
      value: additionalData?.scenarioModule?.defaultScenarioModuleDescription,
    });
  }

  return rows.filter(Boolean) as SummaryGridRow[];
};
